import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Phone,
  Users,
  UserPlus,
  Clock,
  CalendarDays,
  FileCheck,
  TrendingUp,
  Globe,
} from "lucide-react";
import { getCurrentSydneyDisplay } from "../../utils/timezone";

export default function AdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverTime, setServerTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(getCurrentSydneyDisplay());

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, timeRes] = await Promise.all([
          api().get("/dashboard/admin"),
          api().get("/server-time"),
        ]);
        setStats(statsRes.data);
        setServerTime(timeRes.data);
      } catch (error) {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    
    // Update time every second
    const interval = setInterval(() => {
      setCurrentTime(getCurrentSydneyDisplay());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const metricCards = [
    {
      label: "Total Calls",
      value: stats?.total_calls || 0,
      subLabel: "This Week",
      icon: Phone,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      label: "Potential Leads",
      value: stats?.total_potential || 0,
      subLabel: "This Week",
      icon: Users,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      label: "Total Signups",
      value: stats?.total_signups || 0,
      subLabel: "This Week",
      icon: UserPlus,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      label: "Hours Worked",
      value: `${stats?.total_hours || 0}h`,
      subLabel: "This Week",
      icon: Clock,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      label: "Pending Leaves",
      value: stats?.pending_leaves || 0,
      subLabel: "Awaiting Approval",
      icon: CalendarDays,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      label: "Pending Timesheets",
      value: stats?.pending_timesheets || 0,
      subLabel: "Awaiting Review",
      icon: FileCheck,
      color: "text-cyan-600",
      bgColor: "bg-cyan-50",
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
        <p className="text-slate-500 mt-1">Overview of team performance and pending actions</p>
        <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
          <Globe className="h-3 w-3" />
          Sydney Time: {currentTime}
        </p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {metricCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card
              key={metric.label}
              className={`metric-card card-hover animate-fade-in stagger-${index + 1}`}
              data-testid={`admin-metric-${metric.label.toLowerCase().replace(" ", "-")}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="metric-label">{metric.label}</p>
                    <p className="metric-value mt-2">{metric.value}</p>
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {metric.subLabel}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${metric.bgColor}`}>
                    <Icon className={`h-5 w-5 ${metric.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calls Per Staff */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Performance by Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.calls_per_staff || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="calls" name="Calls" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="potential" name="Potential" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="signups" name="Signups" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Daily Signups Trend */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Activity Trend (7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.daily_signups || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    stroke="#94a3b8"
                    tickFormatter={(val) => new Date(val).toLocaleDateString("en-US", { weekday: "short" })}
                  />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    labelFormatter={(val) => new Date(val).toLocaleDateString()}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Line type="monotone" dataKey="calls" name="Calls" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="signups" name="Signups" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hours Per Staff */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Hours Worked by Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.hours_per_staff || []} layout="vertical" margin={{ top: 10, right: 30, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} stroke="#94a3b8" width={80} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                    formatter={(value) => [`${value}h`, "Hours"]}
                  />
                  <Bar dataKey="hours" name="Hours" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
